import { Card, Flex } from '@brightdrop/bd-ui';
import { type MRT_ColumnDef, Table } from '@brightdrop/table-ui';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import CheckIcon from '~/assets/icons/new/checkCircle.svg?react';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import AdaptiveTooltipDrawer from '~/common/components/tooltips/AdaptiveTooltipDrawer';
import type { ManageSectionProps } from '~/features/integrations/partners/types';

import useStyles, { getTableContainerProps } from './DataSharedCard.styles';
import useOverviewCardStyles from './overview-card.styles';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.dataSharedTitle',
  description: 'integrations:account.manage.dataSharedDescription',
  dataType: 'integrations:account.manage.dataType',
  shared: 'integrations:account.manage.shared',
  sharedValue: 'integrations:account.manage.sharedValue',
} as const;

type SharedDataRow = {
  key: string;
  dataType: string;
};

// Data type takes the free space; Shared? drops MRT's default column size and shrinks to its text.
const DATA_TYPE_CELL_SX = { width: '100%' } as const;
const SHARED_CELL_SX = {
  width: 'auto',
  minWidth: 0,
  whiteSpace: 'nowrap',
} as const;

/**
 * Data this integration sends to the partner account. Every listed row is shared.
 */
const DataSharedCard = ({ partner }: ManageSectionProps): JSX.Element => {
  const { classes } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  const { t } = useTranslation('integrations');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { manage, nameKey } = partner;
  const partnerName = translateText(nameKey);
  // The copy may carry <List>/<Item> markup; the accessible name is its plain text.
  const tooltipLabel = translateText(manage.dataSharedTooltipKey)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const rows: SharedDataRow[] = manage.dataSharedRows.map((row) => ({
    key: row.key,
    dataType: translateText(row.labelKey),
  }));
  const sharedLabel = translateText(TRANSLATION_KEYS.sharedValue);

  const columns: MRT_ColumnDef<SharedDataRow>[] = [
    {
      accessorKey: 'dataType',
      header: translateText(TRANSLATION_KEYS.dataType),
      muiTableHeadCellProps: { sx: DATA_TYPE_CELL_SX },
      muiTableBodyCellProps: { sx: DATA_TYPE_CELL_SX },
    },
    {
      id: 'shared',
      header: translateText(TRANSLATION_KEYS.shared),
      Cell: () => (
        <CheckIcon
          role="img"
          aria-label={sharedLabel}
          className={classes.sharedIcon}
        />
      ),
      muiTableHeadCellProps: { align: 'center', sx: SHARED_CELL_SX },
      muiTableBodyCellProps: { align: 'center', sx: SHARED_CELL_SX },
    },
  ];

  return (
    <Card dataTestId="account-integrations-data-shared">
      <Flex col gap={4} className={classes.header}>
        <Flex className={classes.titleRow}>
          <TranslatedLabel
            translationKey={TRANSLATION_KEYS.title}
            variables={{ partner: partnerName }}
            typographyVariant="header2_medium"
            className={cardClasses.title}
            component="h2"
          />
          <AdaptiveTooltipDrawer
            isMobile={isMobile}
            placement="top"
            ariaLabel={tooltipLabel}
            icon={<InfoIcon />}
            content={
              <Typography variant="small_body_regular" component="div">
                <Trans
                  t={t}
                  i18nKey={manage.dataSharedTooltipKey}
                  components={{
                    List: <ul className={classes.tooltipList} />,
                    Item: <li />,
                  }}
                />
              </Typography>
            }
            drawerTitle={translateText(TRANSLATION_KEYS.title, {
              partner: partnerName,
            })}
            className={cardClasses.tooltipIcon}
            dataTestId="account-integrations-data-shared-tooltip"
          />
        </Flex>
        <TranslatedLabel
          translationKey={TRANSLATION_KEYS.description}
          variables={{ partner: partnerName }}
          typographyVariant="small_body_regular"
          className={cardClasses.secondary}
        />
      </Flex>
      <Box className={classes.tableWrapper}>
        <Table
          renderNoRecordFound={<span />}
          tablePropsObj={{
            columns,
            data: rows,
            enableTopToolbar: false,
            enableBottomToolbar: false,
            enablePagination: false,
            muiTableContainerProps: getTableContainerProps(),
          }}
        />
      </Box>
    </Card>
  );
};

export default Object.assign(DataSharedCard, {
  sectionId: 'data-shared',
});
